import type { SubAccountPositionLimit } from '@talos/kyoko';
import type { CrossAssetRiskGroup } from './crossAssetRiskGroupsApi';

export const MatchTargetType = {
  SingleAsset: 'SingleAsset',
  CrossAssetRiskGroup: 'CrossAssetRiskGroup',
} as const;

export type MatchTargetType = (typeof MatchTargetType)[keyof typeof MatchTargetType];

export const MATCH_TARGET_TYPE_LABELS: Record<MatchTargetType, string> = {
  [MatchTargetType.SingleAsset]: 'Single Asset',
  [MatchTargetType.CrossAssetRiskGroup]: 'Cross-Asset Risk Group',
};

export function isCrossAssetRiskGroupLimit(limit: Partial<SubAccountPositionLimit> | undefined): boolean {
  const group = limit?.MatchCrossAssetRiskGroup;
  // API schema reserves "0" for single-asset limits that use MatchAssetID instead of a risk group.
  // https://github.com/talostrading/Ava-UI/blob/main/packages/kyoko/src/types/types.ts#L24534
  return group != null && group !== '0';
}

export function getMatchTargetType(limit: Partial<SubAccountPositionLimit> | undefined): MatchTargetType {
  return isCrossAssetRiskGroupLimit(limit) ? MatchTargetType.CrossAssetRiskGroup : MatchTargetType.SingleAsset;
}

export function getCrossAssetRiskGroupLabel(group: Pick<CrossAssetRiskGroup, 'Name' | 'DisplayName'> | undefined) {
  return group?.DisplayName || group?.Name || '';
}

/** Updates a draft position limit with fields valid for the selected match target type. */
export function setMatchTargetType(limit: Partial<SubAccountPositionLimit>, matchTargetType: MatchTargetType) {
  const previousMatchTargetType = getMatchTargetType(limit);
  if (previousMatchTargetType !== matchTargetType) {
    // Threshold asset options depend on it being Single or Group, so clear stale selection when switching modes.
    delete limit.ThresholdAsset;
  }

  if (matchTargetType === MatchTargetType.CrossAssetRiskGroup) {
    delete limit.MatchAsset;
    if (previousMatchTargetType !== matchTargetType) {
      // Initialize cross-asset mode before a group is chosen.
      limit.MatchCrossAssetRiskGroup = '';
    }
    return;
  }

  delete limit.MatchCrossAssetRiskGroup;
}

/** Builds a position-limit API payload with mutually exclusive match-target fields. */
export function getSubAccountPositionLimitPayload(
  limit: Partial<SubAccountPositionLimit>,
  enableCrossAssetRiskGroupPositionLimits = false
) {
  const payload = { ...limit };
  const group = payload.MatchCrossAssetRiskGroup;

  if (!enableCrossAssetRiskGroupPositionLimits) {
    delete payload.MatchCrossAssetRiskGroup;
    return payload;
  }

  if (isCrossAssetRiskGroupLimit(payload)) {
    delete payload.MatchAsset;
    // This blotter stores an empty string while cross-asset mode is selected but no group is chosen.
    // Do not send that placeholder value to the API.
    if (!group) {
      delete payload.MatchCrossAssetRiskGroup;
    }
    return payload;
  }

  delete payload.MatchCrossAssetRiskGroup;
  return payload;
}
